/**
 * Language Switcher Component
 * Toggle between English and Hindi
 */

import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { cn } from "@/lib/utils";

export const LANGUAGES = [
	{ code: "en", label: "EN" },
	{ code: "hi", label: "HI" },
] as const;

/** The current language and a setter that saves the preference. */
export function useLanguageChoice(): {
	currentLang: "en" | "hi";
	changeLanguage: (lang: "en" | "hi") => Promise<void>;
} {
	const { i18n, t } = useTranslation();
	const currentLang = i18n.language?.startsWith("hi") ? "hi" : "en";
	const setLanguagePreference = useAuthStore(
		(state) => state.setLanguagePreference,
	);

	const changeLanguage = async (lang: "en" | "hi"): Promise<void> => {
		if (lang === currentLang) return;
		try {
			await setLanguagePreference(lang);
			await i18n.changeLanguage(lang);
		} catch {
			toast.error(t("settings.languageUpdateFailed"));
		}
	};

	return { currentLang, changeLanguage };
}

export function LanguageSwitcher({ className }: { className?: string }) {
	const { currentLang, changeLanguage } = useLanguageChoice();

	return (
		<div
			className={cn(
				"inline-flex rounded-lg border border-input bg-card p-1",
				className,
			)}
		>
			{LANGUAGES.map((lang) => (
				<button
					key={lang.code}
					type="button"
					onClick={() => changeLanguage(lang.code)}
					className={cn(
						"rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
						currentLang === lang.code
							? "bg-primary text-primary-foreground"
							: "text-muted-foreground hover:text-foreground",
					)}
				>
					{lang.label}
				</button>
			))}
		</div>
	);
}
